import { useInfiniteQuery } from '@tanstack/react-query';
import { request, gql } from 'graphql-request';

import { QueryKeys } from 'src/constants/queryKeys';

import { TransactionV4 } from '@app/types/transaction';
import { getConfig } from '@app/config';
import { useChainId } from '@thirdweb-dev/react';

export type ViewFilter = 'all' | 'swap' | 'stake' | 'liquidity';

type QueryParams = {
  view: ViewFilter;
  address: string | undefined;
};

type PageParams = {
  skip: number;
};

const PAGE_SIZE = 20;

function getQuery(
  view: ViewFilter,
  pageParams: PageParams,
  queryParams: QueryParams
) {
  switch (view) {
    case 'swap': {
      return gql`
      query {
        transactions(
          orderBy: timestamp
          orderDirection: desc
          skip: ${pageParams?.skip ?? 0},
          first: ${PAGE_SIZE},
          where: {
            swaps_: { recipient: "${queryParams.address?.toLowerCase()}" }
          }
        ) {
          id
          timestamp
          swaps {
            recipient
            amount0
            amount1
            token0 {
              id
              symbol
            }
            token1 {
              id
              symbol
            }
            amountUSD
            pool {
              token0 {
                symbol
              }
              token1 {
                symbol
              }
            }
          }
        }
      }
    `;
    }
    case 'liquidity': {
      return gql`
      query {
        transactions(
          orderBy: timestamp
          orderDirection: desc
          skip: ${pageParams?.skip ?? 0},
          first: ${PAGE_SIZE},
          where: {
            or: [
              {
                mints_: { origin: "${queryParams.address?.toLowerCase()}" }
              }
            ]
          }
        ) {
          id
          timestamp
          mints {
            id
            origin
            amount0
            amount1
            token0 {
              symbol
            }
            token1 {
              symbol
            }
            pool {
              token0 {
                symbol
              }
              token1 {
                symbol
              }
            }
          }
        }
      }
    `;
    }
    default: {
      return gql`
      query {
        transactions(
          orderBy: timestamp
          orderDirection: desc
          skip: ${pageParams?.skip ?? 0},
          first: ${PAGE_SIZE},
          where: {
            or: [
              {
                mints_: { origin: "${queryParams.address?.toLowerCase()}" }
              },
              {
                swaps_: { recipient: "${queryParams.address?.toLowerCase()}" }
              }
            ]
          }
        ) {
          id
          timestamp
          mints {
            id
            origin
            amount0
            amount1
            token0 {
              symbol
            }
            token1 {
              symbol
            }
            pool {
              token0 {
                symbol
              }
              token1 {
                symbol
              }
            }
          }
          swaps {
            recipient
            amount0
            amount1
            token0 {
              id
              symbol
            }
            token1 {
              id
              symbol
            }
            amountUSD
            pool {
              token0 {
                symbol
              }
              token1 {
                symbol
              }
            }
          }
        }
      }
    `;
    }
  }
}

const fetcher = async (
  pageParams: PageParams,
  queryParams: QueryParams,
  chainId: number | undefined
) => {
  const query = getQuery(queryParams.view, pageParams, queryParams);

  const config = getConfig(chainId);

  const res = await request<{ transactions: TransactionV4[] }>(
    `${config?.URLS.subgraphUrlV3}`,
    query,
    queryParams
  );

  // console.log(res);

  return {
    transactions: res?.transactions
  };
};

export function useInfiniteTransactionsHistoryV4(params: QueryParams) {
  const chainId = useChainId();

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    refetch
  } = useInfiniteQuery<{
    transactions: TransactionV4[];
  }>(
    [QueryKeys.TRANSACTIONS_V4, { params, chainId }],
    ({ pageParam }) => {
      return fetcher(pageParam, params, chainId);
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        return {
          skip: allPages.length * PAGE_SIZE
        };
      },
      staleTime: 60000,
      refetchOnWindowFocus: false,
      enabled: !!params.address
    }
  );

  const dataFlat = data?.pages.reduce<TransactionV4[]>((res, page) => {
    res.push(...page.transactions);

    return res;
  }, []);

  const totalLoaded =
    data?.pages.reduce((res, page) => {
      const pageLength = page.transactions.length;

      return res + pageLength;
    }, 0) ?? 0;

  return {
    dataFlat,
    totalLoaded,
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    refetch
  };
}
